import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { BattleSideReveal } from "@/types/battle"
import { displayCardBS } from "@/constants/game.constants"
import { ELEMENT_NAMES, STYLE_ICONS } from "@/constants/guardians"
import { resolveCardHeroImage } from "@/constants/cardAssets"
import { calcEffectiveBS } from "@/constants/game.constants"

interface RevealSequenceProps {
  yourSide: BattleSideReveal
  opponentSide: BattleSideReveal
  onComplete: () => void
}

type Step = "countdown" | "reveal" | "compare" | "total"

export default function RevealSequence({
  yourSide,
  opponentSide,
  onComplete,
}: RevealSequenceProps) {
  const [step, setStep] = useState<Step>("countdown")
  const [count, setCount] = useState(3)

  const yourBs = useMemo(
    () => calcEffectiveBS(yourSide.combat),
    [yourSide.combat]
  )
  const oppBs = useMemo(
    () => calcEffectiveBS(opponentSide.combat),
    [opponentSide.combat]
  )

  useEffect(() => {
    if (step !== "countdown") return
    if (count <= 0) {
      setStep("reveal")
      return
    }
    const t = window.setTimeout(() => setCount((c) => c - 1), 450)
    return () => clearTimeout(t)
  }, [step, count])

  useEffect(() => {
    if (step === "reveal") {
      const t = window.setTimeout(() => setStep("compare"), 1200)
      return () => clearTimeout(t)
    }
    if (step === "compare") {
      const t = window.setTimeout(() => setStep("total"), 2200)
      return () => clearTimeout(t)
    }
    if (step === "total") {
      const t = window.setTimeout(onComplete, 1800)
      return () => clearTimeout(t)
    }
  }, [step, onComplete])

  function CardFace({ side }: { side: BattleSideReveal }) {
    const hero = resolveCardHeroImage(side.card)
    const src = typeof hero === "string" ? hero : ""
    return (
      <div className="rounded-xl border border-white/15 bg-black/30 p-2 text-center text-xs">
        {src ? (
          <img src={src} alt="" className="mx-auto mb-2 aspect-[4/3] w-full max-w-[140px] rounded-lg object-cover" />
        ) : null}
        <p className="font-medium">{side.player.nickname}</p>
        <p className="text-guardians-muted">
          {ELEMENT_NAMES[side.card.element]} {STYLE_ICONS[side.card.style]} BS{" "}
          {displayCardBS(side.card)}
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-2 py-6">
      <AnimatePresence mode="wait">
        {step === "countdown" && count > 0 ? (
          <motion.p
            key={count}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.4, opacity: 0 }}
            className="text-5xl font-bold text-guardians-accent"
          >
            {count}
          </motion.p>
        ) : null}
      </AnimatePresence>

      {(step === "reveal" || step === "compare" || step === "total") && (
        <div className="grid w-full max-w-md grid-cols-2 gap-3">
          <CardFace side={yourSide} />
          <CardFace side={opponentSide} />
        </div>
      )}

      {step === "compare" ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-1 rounded-xl border border-white/10 p-3 text-xs"
        >
          <p>
            База: {yourSide.combat.baseBS} vs {opponentSide.combat.baseBS}
          </p>
          <p>
            Эффективный BS: {yourBs} vs {oppBs}
          </p>
        </motion.div>
      ) : null}

      {step === "total" ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <p className="text-lg font-bold text-guardians-accent">
            {yourBs} — {oppBs}
          </p>
          <p className="mt-2 text-sm">
            {yourSide.outcome === "WIN"
              ? "Победа"
              : yourSide.outcome === "LOSE"
                ? "Поражение"
                : "Ничья"}
          </p>
        </motion.div>
      ) : null}
    </div>
  )
}
